/**
 * Insight / "Know yourself" emotion model: category, UI hue, and valence score for charts.
 * Scores range from -6 (most negative) to +5 (most positive).
 */

export type InsightHue = 'orange' | 'green' | 'gray' | 'red' | 'magenta' | 'blue';

export type InsightCategoryId =
  | 'energeticPositive'
  | 'calmPositive'
  | 'neutralLow'
  | 'angerStress'
  | 'fearInsecurity'
  | 'sadnessShame';

export type InsightEmotionDef = {
  id: string;
  categoryId: InsightCategoryId;
  hue: InsightHue;
  /** Valence for mood line chart Y mapping */
  score: number;
};

/** Canonical list (order = default grid order). */
export const INSIGHT_EMOTIONS: InsightEmotionDef[] = [
  { id: 'loving', categoryId: 'energeticPositive', hue: 'orange', score: 3 },
  { id: 'excited', categoryId: 'energeticPositive', hue: 'orange', score: 5 },
  { id: 'confident', categoryId: 'energeticPositive', hue: 'orange', score: 4 },
  { id: 'joyful', categoryId: 'energeticPositive', hue: 'orange', score: 5 },
  { id: 'happy', categoryId: 'energeticPositive', hue: 'orange', score: 4 },
  { id: 'proud', categoryId: 'energeticPositive', hue: 'orange', score: 3 },
  { id: 'optimistic', categoryId: 'calmPositive', hue: 'green', score: 3 },
  { id: 'calm', categoryId: 'calmPositive', hue: 'green', score: 1 },
  { id: 'grateful', categoryId: 'calmPositive', hue: 'green', score: 5 },
  { id: 'tired', categoryId: 'neutralLow', hue: 'gray', score: -1 },
  { id: 'okay', categoryId: 'neutralLow', hue: 'gray', score: 0 },
  { id: 'numb', categoryId: 'neutralLow', hue: 'gray', score: -1 },
  { id: 'bored', categoryId: 'neutralLow', hue: 'gray', score: -2 },
  { id: 'annoyed', categoryId: 'angerStress', hue: 'red', score: -2 },
  { id: 'angry', categoryId: 'angerStress', hue: 'red', score: -5 },
  { id: 'overwhelmed', categoryId: 'angerStress', hue: 'red', score: -5 },
  { id: 'frustrated', categoryId: 'angerStress', hue: 'red', score: -3 },
  { id: 'stressed', categoryId: 'angerStress', hue: 'red', score: -4 },
  { id: 'anxious', categoryId: 'angerStress', hue: 'red', score: -3 },
  { id: 'insecure', categoryId: 'fearInsecurity', hue: 'magenta', score: -4 },
  { id: 'afraid', categoryId: 'fearInsecurity', hue: 'magenta', score: -5 },
  { id: 'sad', categoryId: 'sadnessShame', hue: 'blue', score: -3 },
  { id: 'ashamed', categoryId: 'sadnessShame', hue: 'blue', score: -6 },
  { id: 'guilty', categoryId: 'sadnessShame', hue: 'blue', score: -4 },
  { id: 'depressed', categoryId: 'sadnessShame', hue: 'blue', score: -5 },
];

const SCORE_MIN = -6;
const SCORE_MAX = 5;

/** Map insight score to chart Y normalization: bottom = SCORE_MIN, top = SCORE_MAX. */
export function moodNormalizedFromInsightScore(score: number): number {
  const clamped = Math.min(SCORE_MAX, Math.max(SCORE_MIN, score));
  return (clamped - SCORE_MIN) / (SCORE_MAX - SCORE_MIN);
}

export const INSIGHT_SCORE_MIN = SCORE_MIN;
export const INSIGHT_SCORE_MAX = SCORE_MAX;

function normLabel(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .trim();
}

/**
 * (pattern, score) pairs; longer patterns first so specific phrases win over short roots
 * (e.g. "depressed" before "sad", "overwhelmed" before "whelmed").
 */
const INSIGHT_LABEL_MATCHERS: { pattern: string; score: number }[] = (() => {
  const pairs: { pattern: string; score: number }[] = [
    { pattern: 'overwhelmed', score: -5 },
    { pattern: 'frustration', score: -3 },
    { pattern: 'frustrated', score: -3 },
    { pattern: 'depression', score: -5 },
    { pattern: 'depressed', score: -5 },
    { pattern: 'optimistic', score: 3 },
    { pattern: 'optimism', score: 3 },
    { pattern: 'confidence', score: 4 },
    { pattern: 'confident', score: 4 },
    { pattern: 'exhaustion', score: -1 },
    { pattern: 'exhausted', score: -1 },
    { pattern: 'gratitude', score: 5 },
    { pattern: 'thankful', score: 5 },
    { pattern: 'grateful', score: 5 },
    { pattern: 'insecurity', score: -4 },
    { pattern: 'insecure', score: -4 },
    { pattern: 'annoyance', score: -2 },
    { pattern: 'annoyed', score: -2 },
    { pattern: 'ashamed', score: -6 },
    { pattern: 'excitement', score: 5 },
    { pattern: 'excited', score: 5 },
    { pattern: 'happiness', score: 4 },
    { pattern: 'happily', score: 4 },
    { pattern: 'happy', score: 4 },
    { pattern: 'joyful', score: 5 },
    { pattern: 'loving', score: 3 },
    { pattern: 'peaceful', score: 1 },
    { pattern: 'peace', score: 1 },
    { pattern: 'numbness', score: -1 },
    { pattern: 'boredom', score: -2 },
    { pattern: 'fearful', score: -5 },
    { pattern: 'scared', score: -5 },
    { pattern: 'afraid', score: -5 },
    { pattern: 'anxiety', score: -3 },
    { pattern: 'anxious', score: -3 },
    { pattern: 'stressed', score: -4 },
    { pattern: 'stress', score: -4 },
    { pattern: 'pressure', score: -4 },
    { pattern: 'burnout', score: -4 },
    { pattern: 'frazzled', score: -4 },
    { pattern: 'tired', score: -1 },
    { pattern: 'sleepy', score: -1 },
    { pattern: 'drained', score: -1 },
    { pattern: 'hopeful', score: 3 },
    { pattern: 'hope', score: 3 },
    { pattern: 'proud', score: 3 },
    { pattern: 'pride', score: 3 },
    { pattern: 'joy', score: 5 },
    { pattern: 'rage', score: -5 },
    { pattern: 'furious', score: -5 },
    { pattern: 'angry', score: -5 },
    { pattern: 'anger', score: -5 },
    { pattern: 'irritat', score: -2 },
    { pattern: 'mad', score: -5 },
    { pattern: 'sorrow', score: -3 },
    { pattern: 'grief', score: -3 },
    { pattern: 'tear', score: -3 },
    { pattern: 'lonely', score: -3 },
    { pattern: 'blue', score: -3 },
    { pattern: 'sad', score: -3 },
    { pattern: 'guilt', score: -4 },
    { pattern: 'guilty', score: -4 },
    { pattern: 'shame', score: -6 },
    { pattern: 'okay', score: 0 },
    { pattern: 'numb', score: -1 },
    { pattern: 'bored', score: -2 },
    { pattern: 'calm', score: 1 },
    { pattern: 'contentment', score: 2 },
    { pattern: 'relief', score: 3 },
    { pattern: 'relieved', score: 3 },
  ];

  pairs.sort((a, b) => b.pattern.length - a.pattern.length);
  return pairs;
})();

/**
 * Best-effort score from journal / model primary emotion label.
 * Returns null if nothing matches (caller may fall back to coarse buckets).
 */
export function insightScoreFromLabel(rawLabel: string): number | null {
  const s = normLabel(rawLabel);
  if (!s) return null;
  for (const { pattern, score } of INSIGHT_LABEL_MATCHERS) {
    if (s.includes(pattern)) return score;
  }
  return null;
}

/**
 * Map a server/model primary emotion string to a canonical `INSIGHT_EMOTIONS` id for i18n
 * (`library.knowYourself.emotion.<id>`). Longest id wins so e.g. "overwhelmed" beats "whelmed".
 */
export function insightEmotionIdFromLabel(rawLabel: string): string | null {
  const s = normLabel(rawLabel);
  if (!s) return null;
  const sorted = [...INSIGHT_EMOTIONS].sort((a, b) => b.id.length - a.id.length);
  for (const em of sorted) {
    if (s.includes(em.id)) return em.id;
  }
  return null;
}

/**
 * Prefer Know Yourself translations; if no id matches, show the raw server label.
 */
export function translateInsightEmotionLabel(
  t: (key: string, params?: Record<string, string>) => string,
  rawLabel: string,
): string {
  const trimmed = rawLabel.trim();
  if (!trimmed) return '';
  const id = insightEmotionIdFromLabel(trimmed);
  if (id) {
    const key = `library.knowYourself.emotion.${id}`;
    const out = t(key);
    if (out !== key) return out;
  }
  return trimmed;
}
